import { APIOutput, Navigation } from "@amzn/ask-legal-domain";
import { StatusIndicator } from "@amzn/awsui-components-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMinus, faPlus, faTimes } from "@fortawesome/free-solid-svg-icons";
import * as React from "react";
import { useLocation } from "react-router-dom";
import { AppContext } from "../../setup/context";
import { getNavigationTabContent } from "../home-page-widgets/navigation-widget";
import "../../styles/component/layout/AppHeader.scss";

export interface AppNavigationHeaderProps {
    instanceId: string;
    isMenuOpen: boolean;
}

const NO_EXPANDED_ITEMS: ReadonlySet<string> = new Set<string>();

const toggleSetMember = (set: ReadonlySet<string>, id: string): ReadonlySet<string> => {
    const next = new Set(set);
    if (next.has(id)) {
        next.delete(id);
    } else {
        next.add(id);
    }
    return next;
};

/** Ids of every item in a tab that has nested content and can therefore be expanded. */
const getExpandableIds = (tab: Navigation.NavTab) => ({
    navGroupIds: tab.items
        .filter(group => group.nested.length > 0)
        .map(group => group.id),
    navLinkIds: tab.items
        .flatMap(group => group.nested)
        .filter(link => link.nested?.length > 0)
        .map(link => link.id)
});

export const AppNavigationHeader = (props: AppNavigationHeaderProps) => {
    const context = React.useContext(AppContext);
    const [loadingProps, setLoadingProps] = React.useState<{}>();
    const [errorProps, setErrorProps] = React.useState<Error>();
    const [navigationData, setNavigationData] = React.useState<Navigation.Data>();
    const [activeNavTab, setActiveNavTab] = React.useState<string>(null);
    // Sets (not single ids) so that several groups/links can be open at once, which "Expand All" requires.
    const [expandedNavGroups, setExpandedNavGroups] = React.useState<ReadonlySet<string>>(NO_EXPANDED_ITEMS);
    const [expandedNavLinks, setExpandedNavLinks] = React.useState<ReadonlySet<string>>(NO_EXPANDED_ITEMS);
    const navRef = React.useRef<HTMLElement>(null);
    const location = useLocation();

    const collapseAll = React.useCallback(() => {
        setExpandedNavGroups(NO_EXPANDED_ITEMS);
        setExpandedNavLinks(NO_EXPANDED_ITEMS);
    }, []);

    const closeNavMenu = React.useCallback(() => {
        setActiveNavTab(null);
        collapseAll();
    }, [collapseAll]);

    // While a tab is open, close it on any pointer press outside the open panel and the tab buttons.
    // Tab buttons count as inside because they toggle themselves via onClick; closing on their
    // pointerdown would make the subsequent onClick immediately reopen the tab.
    // Capture phase so components that stopPropagation cannot prevent the close.
    React.useEffect(() => {
        if (!activeNavTab) return;
        const handlePointerDown = (event: PointerEvent) => {
            const target = event.target;
            const isInside = target instanceof Element &&
                !!navRef.current?.contains(target) &&
                !!target.closest(".navtab-content, .nav-old-tabname");
            if (!isInside) {
                closeNavMenu();
            }
        };
        document.addEventListener("pointerdown", handlePointerDown, true);
        return () => document.removeEventListener("pointerdown", handlePointerDown, true);
    }, [activeNavTab, closeNavMenu]);

    // The header stays mounted across navigations within the same route (e.g. live page -> live page),
    // so close the menu when an in-app link changes the location. New-tab links do not change location.
    React.useEffect(() => {
        closeNavMenu();
    }, [location.pathname, location.search, closeNavMenu]);

    // Opening, closing or switching tabs always starts the panel fully collapsed.
    const handleActiveNavTab = (id: string) => {
        if (activeNavTab === id) {
            closeNavMenu();
        } else {
            setActiveNavTab(id);
            collapseAll();
        }
    };

    // Each group/link toggles independently; opening one does not collapse the others.
    const handleActiveNavGroup = (id: string) => {
        setExpandedNavGroups(current => toggleSetMember(current, id));
    };

    const handleActiveNavLink = (id: string) => {
        setExpandedNavLinks(current => toggleSetMember(current, id));
    };

    const isTabFullyExpanded = (tab: Navigation.NavTab) => {
        const { navGroupIds, navLinkIds } = getExpandableIds(tab);
        return navGroupIds.every(id => expandedNavGroups.has(id)) &&
            navLinkIds.every(id => expandedNavLinks.has(id));
    };

    const handleToggleExpandAll = (tab: Navigation.NavTab) => {
        if (isTabFullyExpanded(tab)) {
            collapseAll();
        } else {
            const { navGroupIds, navLinkIds } = getExpandableIds(tab);
            setExpandedNavGroups(new Set(navGroupIds));
            setExpandedNavLinks(new Set(navLinkIds));
        }
    };

    const handleClosePanel = (tab: Navigation.NavTab) => {
        closeNavMenu();
        // Return keyboard focus to the tab button that opened the panel.
        document.getElementById(tab.id)?.focus();
    };

    const renderPanelToolbar = (tab: Navigation.NavTab) => {
        const { navGroupIds, navLinkIds } = getExpandableIds(tab);
        const hasExpandableItems = navGroupIds.length > 0 || navLinkIds.length > 0;
        const isFullyExpanded = hasExpandableItems && isTabFullyExpanded(tab);
        return (
            <div className="navtab-toolbar">
                {hasExpandableItems && (
                    <button
                        type="button"
                        className="navtab-toolbar-button"
                        onClick={() => handleToggleExpandAll(tab)}
                    >
                        <FontAwesomeIcon icon={isFullyExpanded ? faMinus : faPlus} />
                        <span>{isFullyExpanded ? "Collapse All" : "Expand All"}</span>
                    </button>
                )}
                <button
                    type="button"
                    className="navtab-toolbar-button navtab-toolbar-close"
                    aria-label={`Close ${tab.name} menu`}
                    title="Close"
                    onClick={() => handleClosePanel(tab)}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        );
    };

    const init = async () => {
        setLoadingProps({});
        setNavigationData(undefined);
        try {
            const loadNavigationOutput = await context.getNavigationAPI().load({
                navigationId: props.instanceId,
            });
            const output = APIOutput.fromRaw<Navigation.Data>(loadNavigationOutput.data);
            if (output.isOk()) {
                setNavigationData(output.data);
            } else if (output.isErr()) {
                if (output.err.code === 403) {
                    setErrorProps({
                        name: "Forbidden",
                        message: "You do not have permission to access this resource"
                    });
                } else if (output.err.code === 404) {
                    setErrorProps({
                        name: "Not Found",
                        message: "This resource does not exist"
                    });
                } else {
                    setErrorProps({
                        name: "Error",
                        message: output.err.message
                    });
                }
                if (navigationData) {
                    setNavigationData(undefined);
                }
            }
        } catch (error) {
            setErrorProps(error);
            if (navigationData) {
                setNavigationData(undefined);
            }
        } finally {
            setLoadingProps(undefined);
        }
    };


    React.useEffect(() => {
        init();
    }, [props.instanceId]);

    return <>
        <nav ref={navRef} className={`awsui-polaris-dark-mode nav-old-navigation${props.isMenuOpen ? " active" : ""}`}>
            {loadingProps && <>...</>}
            {errorProps && <StatusIndicator type="error">{errorProps.name} : {errorProps.message}</StatusIndicator>}
            {navigationData && (
                navigationData.tabs.map(tab =>
                    <div className="nav-old-navtab" key={tab.id}>
                        <button
                            id={tab.id}
                            className="nav-old-tabname"
                            aria-expanded={activeNavTab === tab.id}
                            aria-controls={tab.name}
                            onClick={() => handleActiveNavTab(tab.id)}
                        >
                            <span className="nav-old-tab-text">{tab.name}</span>
                        </button>
                        <div
                            className={`navtab-content ${activeNavTab === tab.id ? "navtab-display" : ""}`}
                            id={tab.name}
                        >
                            {activeNavTab === tab.id && renderPanelToolbar(tab)}
                            {getNavigationTabContent({
                                tabItems: tab.items,
                                isNavGroupExpanded: (id) => expandedNavGroups.has(id),
                                isNavLinkExpanded: (id) => expandedNavLinks.has(id),
                                handleActiveNavGroup: handleActiveNavGroup,
                                handleActiveNavLink: handleActiveNavLink,
                                tabColumnCount: props.isMenuOpen ? 1 : undefined
                            })}
                        </div>
                    </div>
                )
            )}
        </nav>
    </>;
};
